import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService, NavigateArgs } from 'src/app/wizard-bar/services/wizard.service';
import { of, Observable, forkJoin, Subscription, Subject } from 'rxjs';
import { UtilityAccountsGridComponent } from 'src/app/shared/components/utility-accounts-grid/utility-accounts-grid.component';
import { QuoteRequestState } from 'src/app/shared/models/quote-request-state';
import { UtilityInformationService } from 'src/app/shared/services/utility-information.service';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { Store } from '@ngrx/store';
import { UserRole, UtilityHubIdentity } from 'src/app/auth/models/utility-hub-identity';
import { isEqual } from 'lodash';
import { LoadingModalComponent, LoadingModalData } from '@app/shared/components/loading-modal/loading-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { UtilityAccount } from '@app/shared/models/UtilityAccount';
import { map, mergeMap } from 'rxjs/operators';
import { Logger } from '@app/logging.service';
import { AlertComponent } from '@app/shared/components/alert/alert.component';
import { HttpErrorResponse } from '@angular/common/http';
import LogRocket from 'logrocket';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { AppState } from '@app/state/app.state';
import { salesTypeSelector, utilityUOMSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { RenewalsMatrixConstants } from '../../models/renewals-matrix-constants';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';
import { originalQuoteRequestIdSelector, originalQuoteDueDateSelector } from '@app/renewals/state/selectors/renewals-module.selectors';
import { MatrixService } from '@app/matrix/services/matrix.service';

@Component({
  selector: 'app-renewals-matrix-utility-accounts',
  templateUrl: './renewals-matrix-utility-accounts.component.html',
  styleUrls: ['./renewals-matrix-utility-accounts.component.scss'],
})
export class RenewalsMatrixUtilityAccountsComponent implements OnInit, OnDestroy {
  public quoteRequestId: any;
  public grid: UtilityAccountsGridComponent;
  public quoteRequestState: QuoteRequestState;
  userIdentity: UtilityHubIdentity;
  ownerUserId: string;

  private initialFormValue: UtilityAccount[];
  private logger: Logger;
  isPriceRequested: boolean;
  route$: Subscription;
  dialog$: Subscription;
  selectLoggedInUser$: Subscription;
  uhIdentity$: Subscription;
  saveUtilityAccounts$: Subscription;
  nextButtonClicked: Subject<void> = new Subject<void>();
  commodity: CommodityType;
  commodityType = CommodityType;
  salesType$: Observable<string>;
  utilityUOM$: Subscription;
  uom: string;
  originalQuoteRequestIdSelector$: Subscription;
  originalQuoteRequestId: number | null;
  originalQuoteRequestDueDateSelector$: Subscription;
  originalQuoteRequestDueDate: Date | null;

  constructor(
    private route: ActivatedRoute,
    private wizardService: WizardService,
    public utilityInformationService: UtilityInformationService,
    public quoteRequestService: QuoteRequestService,
    public matrixService: MatrixService,
    private store: Store<AppState>,
    public dialog: MatDialog
  ) {
    this.salesType$ = store.select(salesTypeSelector);
    this.logger = Logger.for(this);
    this.utilityUOM$ = this.store.select(utilityUOMSelector).subscribe((x) => {
      this.uom = x;
    });
  }

  ngOnDestroy(): void {
    this.route$?.unsubscribe();
    this.dialog$?.unsubscribe();
    this.selectLoggedInUser$?.unsubscribe();
    this.uhIdentity$?.unsubscribe();
    this.saveUtilityAccounts$?.unsubscribe();
    this.originalQuoteRequestIdSelector$?.unsubscribe();
    this.originalQuoteRequestDueDateSelector$?.unsubscribe();
  }

  ngOnInit(): void {
    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.originalQuoteRequestId = originalQuoteRequestId;
    });

    this.originalQuoteRequestDueDateSelector$ = this.store.select(originalQuoteDueDateSelector).subscribe((originalQuoteDueDate) => {
      this.originalQuoteRequestDueDate = originalQuoteDueDate;
    });
    this.wizardService.bindStep(this.route, (args) => this.save(args));
    this.route$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = null;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);

        this.selectLoggedInUser$ = this.store.select(loggedInUserSelector).subscribe((user) => {
          if (user && !user.expired) {
            this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
              this.userIdentity = uhUser;
            });
          }
        });

        this.dialog$ = this.dialog
          .open(LoadingModalComponent, {
            width: '25%',
            disableClose: true,
            data: {
              message: `Fetching Load Profiles`,
              unitOfWork: this.load(),
            } as LoadingModalData,
          })
          .afterClosed()
          .subscribe((res) => {
            if (res instanceof Error || res instanceof HttpErrorResponse) {
              this.dialog
                .open(AlertComponent, {
                  width: '25%',
                  data: {
                    title: 'Load Profile Missing',
                    message: 'Load Profiles could not be found for the selected Utility, please select another Utility',
                  },
                })
                .afterClosed()
                .subscribe((_) => this.wizardService.previous());
            } else {
              if (this.userIdentity.isBroker || res.utilityAccounts.filter((ua) => ua.rateCode === "I don't know").length > 0) {
                if (res.rateCodes === undefined || res.rateCodes === null) { 
                  res.rateCodes = []; 
                }
                res.rateCodes.push({ rateCode: "I don't know", rateClass: "I don't know" });
              }
              this.quoteRequestState = res.state;
              this.commodity = res.state.commodity;
              this.isPriceRequested = res.state.status !== QuoteRequestStatus.InProgress;
              this.ownerUserId = res.state.ownerUserId;
              this.grid.populate(res.utilityAccounts, res.utilityInformation, res.state, res.quoteConfiguration);
              this.initialFormValue = this.grid.getDataSnapshot();
            }
          });
      }
    });
  }

  public gridInit(grid: UtilityAccountsGridComponent) {
    this.grid = grid;
  }

  private load(): Observable<any> {
    return forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      utilityAccounts: this.quoteRequestService.getUtilityAccounts(this.quoteRequestId),
      quoteConfiguration: this.matrixService.getQuoteConfiguration(this.quoteRequestId),
    }).pipe(
      mergeMap((res) => {
        this.store.dispatch(new SetOriginalQuoteRequestId(res.state.renewedFromQuoteId));

        return this.utilityInformationService.getUtilityInformationForQuote(res.state).pipe(
          map((utilityInformation) => {
            return { ...res, utilityInformation };
          })
        );
      })
    );
  }

  public isReadOnly() {
    return this.quoteRequestService.isReadonly(this.quoteRequestId, this.quoteRequestState?.status, this.userIdentity?.id, this.quoteRequestState?.ownerUserId);
  }

  public save(args: NavigateArgs): Observable<any> {
    // Form is disabled for SM reviewing a BM quote request, except for the Zone field which is editable
    // A SM needs to be able to save changes to this field despite the form being disabled
    // https://powwr.atlassian.net/browse/IACBPT1-1191
    if (this.isReadOnly() && this.userIdentity.currentOrganisation.role !== UserRole.SupplierManager) {
      return of(true);
    }

    if (!this.isGridDirty()) {
      return of(true);
    }

    return new Observable((obs) => {
      if (this.grid.isValid) {
        const utilityAccounts = this.grid.getData();
        utilityAccounts.forEach((x) => (x.annualUsageUOM = this.uom));
        this.saveUtilityAccounts$ = this.quoteRequestService.saveUtilityAccounts(this.quoteRequestId, utilityAccounts).subscribe(() => {
          LogRocket.track('matrix_utility_accounts_added', { utilityAccountCount: utilityAccounts.length });
          obs.next(true);
          obs.complete();
        });
      } else {
        if (
          (args.direction && args.direction < 0) ||
          this.wizardService.clickedStepIsPreviousStep(RenewalsMatrixConstants.wizardSteps, args.step)
        ) {
          obs.next(true);
        } else {
          obs.next(false);
        }
        obs.complete();
      }
    });
  }

  public next() {
    this.nextButtonClicked.next();
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  public back() {
    this.wizardService.previous(this.isQuoteRequestInvalidated());
  }

  public isQuoteRequestInvalidated() {
    return (
      this.hasFormChanged() && this.quoteRequestState?.status !== QuoteRequestStatus.InProgress && !this.isSupplierManagerValidChange()
    );
  }

  isGridDirty(): boolean {
    // The ticket I'm working on specifies the Matrix route only, so for now I implemented this here.
    // This could go in the utility accounts grid component when the custom route gets this logic,
    // but the definition of dirty here excludes changes from I don't know to a valid value, logic that may not be universal.
    let dirty = true;
    const gridData = this.grid.getData();

    if (gridData.length === 0 && !this.initialFormValue) return false;
    if (gridData.length !== this.initialFormValue?.length) return true;

    if (this.initialFormValue?.length) {
      for (let index = 0; index < this.initialFormValue?.length; index++) {
        const initialRateCode = this.initialFormValue[index].rateCode;
        const finalRateCode = gridData[index]?.rateCode;
  
        if (initialRateCode !== finalRateCode && initialRateCode !== "I don't know") {
          dirty = true;
          break;
        } else {
          dirty = false;
        }
      }
    }
    

    if (this.hasFormChanged()) {
      dirty = true;
    }
    return dirty;
  }

  isSupplierManagerValidChange() {
    if (this.userIdentity.currentOrganisation.role === UserRole.SupplierManager && !this.isGridDirty()) {
      return true;
    }
    return false;
  }

  public hasFormChanged() {
    if (!this.grid) {
      return false;
    }
    return !isEqual(this.initialFormValue, this.grid.getData());
  }
}
